import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTabData } from '../hooks/useTabData';
import { fetchSchedule } from '../services/apiService';
import { SkeletonLoader } from './SkeletonLoader';
import { getDisciplineName } from '../config/disciplineNames';

export const Schedule = ({ isActive }) => {
  const { data, isLoading, error, refetch } = useTabData('schedule', fetchSchedule);
  const showLocation = false;
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [medalOnly, setMedalOnly] = useState(false);
  const eventsListRef = useRef(null);
  const [pendingScrollTarget, setPendingScrollTarget] = useState(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const getDayKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDayLabel = (date) => {
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getNearestDayKey = (dayOptions) => {
    if (!dayOptions.length) return '';

    const now = Date.now();
    return dayOptions.reduce((nearest, option) => {
      if (!nearest) return option;
      const nearestDiff = Math.abs(nearest.timestamp - now);
      const optionDiff = Math.abs(option.timestamp - now);
      return optionDiff < nearestDiff ? option : nearest;
    }, null).key;
  };

  const getDefaultDayKey = (dayOptions, todayDayKey) => {
    if (!dayOptions.length) return '';

    const hasToday = dayOptions.some((option) => option.key === todayDayKey);
    if (hasToday) return todayDayKey;

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const nextUpcoming = dayOptions.find((option) => option.timestamp >= todayStart.getTime());
    if (nextUpcoming) return nextUpcoming.key;

    return getNearestDayKey(dayOptions);
  };

  const events = useMemo(() => {
    if (!data) return [];
    return data
      .filter((event) => event.StartDate && !Number.isNaN(new Date(event.StartDate).getTime()))
      .sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate));
  }, [data]);

  const dayOptions = useMemo(() => {
    const days = new Map();

    events.forEach((event) => {
      const date = new Date(event.StartDate);
      const dayKey = getDayKey(date);

      if (!days.has(dayKey)) {
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        days.set(dayKey, {
          key: dayKey,
          label: formatDayLabel(date),
          timestamp: dayStart
        });
      }
    });

    return [...days.values()].sort((a, b) => a.timestamp - b.timestamp);
  }, [events]);

  const todayDayKey = getDayKey(new Date());

  const nearestDayKey = useMemo(() => getNearestDayKey(dayOptions), [dayOptions]);

  const defaultDayKey = useMemo(() => getDefaultDayKey(dayOptions, todayDayKey), [dayOptions, todayDayKey]);

  const hasTodayEvents = useMemo(() => dayOptions.some((option) => option.key === todayDayKey), [dayOptions, todayDayKey]);

  const disciplineOptions = useMemo(() => {
    const disciplines = [...new Set(events
      .map((event) => event.DisciplineCode)
      .filter(Boolean))];

    return disciplines.sort((a, b) => a.localeCompare(b));
  }, [events]);

  const eventsByDay = useMemo(() => {
    return events.reduce((acc, event) => {
      const dayKey = getDayKey(new Date(event.StartDate));
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(event);
      return acc;
    }, {});
  }, [events]);

  const disciplineStats = useMemo(() => {
    return events.reduce((acc, event) => {
      const code = event.DisciplineCode;
      if (!code) return acc;

      if (!acc[code]) {
        acc[code] = { total: 0, current: 0 };
      }

      acc[code].total += 1;

      const eventDayKey = getDayKey(new Date(event.StartDate));
      if (selectedDay === 'all' || eventDayKey === selectedDay) {
        acc[code].current += 1;
      }

      return acc;
    }, {});
  }, [events, selectedDay]);

  const allDisciplineCount = useMemo(() => {
    if (selectedDay === 'all') return events.length;
    return (eventsByDay[selectedDay] || []).length;
  }, [selectedDay, events, eventsByDay]);

  useEffect(() => {
    if (!dayOptions.length) {
      setSelectedDay('');
      return;
    }

    setSelectedDay((prevDay) => {
      if (prevDay && dayOptions.some((option) => option.key === prevDay)) {
        return prevDay;
      }

      return defaultDayKey;
    });
  }, [dayOptions, defaultDayKey]);

  useEffect(() => {
    setSelectedDiscipline((prevDiscipline) => {
      if (prevDiscipline === 'all') return prevDiscipline;
      if (disciplineOptions.includes(prevDiscipline)) return prevDiscipline;
      return 'all';
    });
  }, [disciplineOptions]);

  const filteredEvents = useMemo(() => {
    if (!selectedDay) return [];
    let dayEvents = selectedDay === 'all' ? events : (eventsByDay[selectedDay] || []);

    if (selectedDiscipline !== 'all') {
      dayEvents = dayEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    if (medalOnly) {
      dayEvents = dayEvents.filter((event) => event.Medal);
    }

    return dayEvents;
  }, [selectedDay, events, eventsByDay, selectedDiscipline, medalOnly]);

  const nextUpcomingEvent = useMemo(() => {
    const now = Date.now();
    let candidateEvents = [...events];

    if (selectedDiscipline !== 'all') {
      candidateEvents = candidateEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    if (medalOnly) {
      candidateEvents = candidateEvents.filter((event) => event.Medal);
    }

    return candidateEvents.find((event) => new Date(event.StartDate).getTime() >= now) || null;
  }, [events, selectedDiscipline, medalOnly]);

  const hasNextUpcomingEvent = Boolean(nextUpcomingEvent);

  const medalEventCount = useMemo(() => {
    let baseEvents = selectedDay === 'all' ? events : (eventsByDay[selectedDay] || []);

    if (selectedDiscipline !== 'all') {
      baseEvents = baseEvents.filter((event) => event.DisciplineCode === selectedDiscipline);
    }

    return baseEvents.filter((event) => event.Medal).length;
  }, [selectedDay, events, eventsByDay, selectedDiscipline]);

  const selectedDaySummary = useMemo(() => {
    if (selectedDay === 'all') return 'All days';
    const selectedOption = dayOptions.find((option) => option.key === selectedDay);
    return selectedOption?.label || 'Day';
  }, [selectedDay, dayOptions]);

  const selectedDisciplineSummary = useMemo(() => {
    if (selectedDiscipline === 'all') return 'All sports';
    return getDisciplineName(selectedDiscipline);
  }, [selectedDiscipline]);

  const hasActiveFilters = useMemo(() => {
    const defaultDay = defaultDayKey || '';
    return selectedDiscipline !== 'all' || medalOnly || (!!selectedDay && selectedDay !== defaultDay);
  }, [selectedDay, selectedDiscipline, medalOnly, defaultDayKey]);

  const resetFilters = () => {
    setSelectedDay(defaultDayKey || 'all');
    setSelectedDiscipline('all');
    setMedalOnly(false);
    setPendingScrollTarget(null);
  };

  const scrollToEvent = (target) => {
    const listEl = eventsListRef.current;
    if (!listEl || !target) return false;

    const cards = listEl.querySelectorAll('.event-card[data-event-code][data-event-start]');
    const eventCard = Array.from(cards).find((card) => {
      return card.dataset.eventCode === target.code && card.dataset.eventStart === target.startDate;
    });

    if (!eventCard) return false;

    try {
      eventCard.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    } catch {
      try {
        eventCard.scrollIntoView(false);
      } catch {
        const listRect = listEl.getBoundingClientRect();
        const cardRect = eventCard.getBoundingClientRect();
        listEl.scrollTop += cardRect.top - listRect.top - 8;
      }
    }

    return true;
  };

  const jumpToNowEvent = () => {
    if (!nextUpcomingEvent?.StartDate) return;

    const target = {
      code: nextUpcomingEvent.Code || '',
      startDate: nextUpcomingEvent.StartDate
    };

    setPendingScrollTarget(target);

    const targetDayKey = getDayKey(new Date(nextUpcomingEvent.StartDate));
    if (selectedDay !== targetDayKey) {
      setSelectedDay(targetDayKey);
      return;
    }

    window.requestAnimationFrame(() => {
      const scrolled = scrollToEvent(target);
      if (scrolled) {
        setPendingScrollTarget(null);
      }
    });
  };

  useEffect(() => {
    if (medalOnly && medalEventCount === 0) {
      setMedalOnly(false);
    }
  }, [medalOnly, medalEventCount]);

  useEffect(() => {
    if (!pendingScrollTarget) return;

    const rafId = window.requestAnimationFrame(() => {
      const scrolled = scrollToEvent(pendingScrollTarget);
      if (scrolled) {
        setPendingScrollTarget(null);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [pendingScrollTarget, filteredEvents, selectedDay]);

  useEffect(() => {
    const listEl = eventsListRef.current;
    if (!listEl) return undefined;

    const updateScrollState = () => {
      const maxScrollTop = listEl.scrollHeight - listEl.clientHeight;
      if (maxScrollTop <= 1) {
        setHasOverflow(false);
        setCanScrollUp(false);
        setCanScrollDown(false);
        return;
      }

      setHasOverflow(true);
      setCanScrollUp(listEl.scrollTop > 1);
      setCanScrollDown(listEl.scrollTop < maxScrollTop - 1);
    };

    const rafId = window.requestAnimationFrame(updateScrollState);
    const settleTimeoutId = window.setTimeout(updateScrollState, 160);
    let resizeObserver;

    if (typeof window.ResizeObserver !== 'undefined') {
      resizeObserver = new window.ResizeObserver(updateScrollState);
      resizeObserver.observe(listEl);
    }

    const stopTouchPropagation = (event) => {
      event.stopPropagation();
    };

    listEl.addEventListener('scroll', updateScrollState, { passive: true });
    listEl.addEventListener('touchstart', stopTouchPropagation, { passive: true });
    listEl.addEventListener('touchmove', stopTouchPropagation, { passive: true });
    window.addEventListener('resize', updateScrollState);
    window.addEventListener('orientationchange', updateScrollState);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimeoutId);
      listEl.removeEventListener('scroll', updateScrollState);
      listEl.removeEventListener('touchstart', stopTouchPropagation);
      listEl.removeEventListener('touchmove', stopTouchPropagation);
      window.removeEventListener('resize', updateScrollState);
      window.removeEventListener('orientationchange', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [filteredEvents.length, selectedDay, selectedDiscipline, medalOnly, isActive]);

  const nudgeEventsList = (direction) => {
    const listEl = eventsListRef.current;
    if (!listEl) return;

    const step = Math.max(140, Math.round(listEl.clientHeight * 0.55));
    listEl.scrollBy({
      top: direction * step,
      behavior: 'smooth'
    });
  };

  const hasScrollableEvents = hasOverflow;

  const hasValidDate = (dateStr) => {
    if (!dateStr) return false;
    return !Number.isNaN(new Date(dateStr).getTime());
  };

  const formatDateTime = (dateStr) => {
    if (!hasValidDate(dateStr)) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeOnly = (dateStr) => {
    if (!hasValidDate(dateStr)) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventTimeRange = (event) => {
    if (event.HideStartDate && event.HideEndDate) {
      return 'Time TBA';
    }

    if (event.HideStartDate) {
      const hiddenStartEndLabel = formatTimeOnly(event.EndDate);
      return hiddenStartEndLabel ? `Ends ${hiddenStartEndLabel}` : 'Start time TBA';
    }

    const startLabel = formatDateTime(event.StartDate);
    if (event.HideEndDate || !hasValidDate(event.EndDate)) return startLabel;

    const endLabel = formatTimeOnly(event.EndDate);
    if (!endLabel) return startLabel;

    return `${startLabel} - ${endLabel}`;
  };

  const getActualTimingNote = (event) => {
    if (!hasValidDate(event.StartDate) || !hasValidDate(event.ActualStartDate)) {
      return '';
    }

    const scheduledMs = new Date(event.StartDate).getTime();
    const actualMs = new Date(event.ActualStartDate).getTime();
    const diffMs = actualMs - scheduledMs;

    if (Math.abs(diffMs) < 60000) {
      return '';
    }

    const timingLabel = diffMs > 0 ? 'Delayed start' : 'Started early';
    const actualTime = formatTimeOnly(event.ActualStartDate);
    return actualTime ? `${timingLabel}: ${actualTime}` : '';
  };

  const getStatusMeta = (statusCode) => {
    const normalized = (statusCode || '').toUpperCase();

    if (normalized === 'RUNNING') {
      return { label: 'Live', variant: 'live' };
    }

    if (normalized === 'FINISHED') {
      return { label: 'Finished', variant: 'finished' };
    }

    if (normalized === 'GETTING_READY') {
      return { label: 'Getting ready', variant: 'ready' };
    }

    if (normalized === 'RESCHEDULED') {
      return { label: 'Rescheduled', variant: 'rescheduled' };
    }

    return { label: 'Scheduled', variant: 'scheduled' };
  };

  if (error) {
    return (
      <div className="tab-content error-state">
        <p className="error-message">Failed to load schedule</p>
        <button className="retry-button" onClick={refetch}>Retry</button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="tab-content loading-state">
        <SkeletonLoader count={5} type="card" />
      </div>
    );
  }

  return (
    <div className="tab-content schedule">
      {dayOptions.length > 0 && (
        <div className="schedule-filter">
          <div className="schedule-filter__group">
            <label htmlFor="schedule-day-select" className="schedule-filter__label">Day</label>
            <div className="schedule-filter__day-controls">
              <div className="schedule-filter__quick-buttons" role="group" aria-label="Quick day filters">
                <button
                  type="button"
                  className={`schedule-filter__quick-button ${selectedDay === 'all' ? 'schedule-filter__quick-button--active' : ''}`}
                  onClick={() => setSelectedDay('all')}
                  aria-pressed={selectedDay === 'all'}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`schedule-filter__quick-button ${selectedDay === todayDayKey ? 'schedule-filter__quick-button--active' : ''}`}
                  onClick={() => {
                    if (hasTodayEvents) setSelectedDay(todayDayKey);
                  }}
                  aria-pressed={selectedDay === todayDayKey}
                  disabled={!hasTodayEvents}
                  title={hasTodayEvents ? 'Jump to today' : 'No events scheduled for today'}
                >
                  Today
                </button>
              </div>
              <select
                id="schedule-day-select"
                className="schedule-filter__select"
                value={selectedDay}
                onChange={(event) => setSelectedDay(event.target.value)}
              >
                <option value="all">All Days ({events.length})</option>
                {dayOptions.map((option) => (
                  <option key={option.key} value={option.key}>{option.label} ({(eventsByDay[option.key] || []).length})</option>
                ))}
              </select>
              <button
                type="button"
                className="schedule-filter__quick-button schedule-filter__quick-button--compact"
                onClick={jumpToNowEvent}
                disabled={!hasNextUpcomingEvent}
                title={hasNextUpcomingEvent ? 'Jump to next upcoming event' : 'No upcoming events for selected filters'}
                aria-label="Jump to next upcoming event"
              >
                Up Next
              </button>
            </div>
          </div>

          <div className="schedule-filter__group">
            <label htmlFor="schedule-discipline-select" className="schedule-filter__label">Sport</label>
            <select
              id="schedule-discipline-select"
              className="schedule-filter__select"
              value={selectedDiscipline}
              onChange={(event) => setSelectedDiscipline(event.target.value)}
            >
              <option value="all">All ({allDisciplineCount})</option>
              {disciplineOptions.map((disciplineCode) => {
                const disciplineName = getDisciplineName(disciplineCode);
                const disciplineCount = disciplineStats[disciplineCode]?.current || 0;
                return (
                  <option key={disciplineCode} value={disciplineCode}>
                    {disciplineName} ({disciplineCount})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="schedule-filter__toggle-row">
            <div className="schedule-filter__toggle-copy">
              <span className="schedule-filter__label">Medal events only</span>
            </div>
            <button
              type="button"
              className={`schedule-filter__switch ${medalOnly ? 'schedule-filter__switch--active' : ''}`}
              onClick={() => setMedalOnly((prev) => !prev)}
              aria-pressed={medalOnly}
              aria-label={`Medal events only ${medalOnly ? 'on' : 'off'}`}
              title={medalOnly ? 'Medal events only is on' : 'Medal events only is off'}
              disabled={medalEventCount === 0}
            >
              <span className="schedule-filter__switch-track">
                <span className="schedule-filter__switch-thumb"></span>
              </span>
              <span className="schedule-filter__switch-text">{medalOnly ? 'On' : 'Off'}</span>
            </button>
          </div>

          <div className="schedule-filter__summary" aria-live="polite">
            <span>
              {filteredEvents.length} events shown · {selectedDaySummary} · {selectedDisciplineSummary} · {medalOnly ? 'Medal only' : 'All events'}
            </span>
            <button
              type="button"
              className={`schedule-filter__clear ${hasActiveFilters ? '' : 'schedule-filter__clear--hidden'}`.trim()}
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              aria-hidden={!hasActiveFilters}
              tabIndex={hasActiveFilters ? 0 : -1}
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      <div className="events-list" ref={eventsListRef}>
        {dayOptions.length === 0 ? (
          <div className="no-events">No scheduled events available</div>
        ) : filteredEvents.length === 0 ? (
          <div className="no-events">
            <p>No events match the selected filters</p>
            <div className="no-events__actions">
              {selectedDiscipline !== 'all' && (
                <button type="button" className="no-events__action" onClick={() => setSelectedDiscipline('all')}>
                  Clear discipline
                </button>
              )}
              {medalOnly && (
                <button type="button" className="no-events__action" onClick={() => setMedalOnly(false)}>
                  Show all events
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredEvents.map((event, idx) => {
            const statusMeta = getStatusMeta(event.ScheduleStatus);
            const actualTimingNote = getActualTimingNote(event);
            const disciplineName = getDisciplineName(event.DisciplineCode) || event.DisciplineCode || 'Discipline';
            const eventTitle = event.EventDescription || 'Event';

            return (
            <div
              key={`${event.Code}-${idx}`}
              className="event-card"
              data-event-code={event.Code || ''}
              data-event-start={event.StartDate || ''}
            >
              <div className="event-badges">
                <div className={`event-status event-status--${statusMeta.variant}`}>
                  {statusMeta.label}
                </div>
                {event.Medal && (
                  <div className="event-medal" aria-label="Medal Event" title="Medal Event">
                    <span className="medal-disc medal-disc--gold"></span>
                    <span className="event-medal__label">Medal Event</span>
                  </div>
                )}
              </div>
              <div className="event-header">
                <div className="event-discipline">
                  <span className="event-discipline__name">{disciplineName}</span>
                </div>
                <div className="event-title">
                  {eventTitle}
                </div>
                <div className="event-phase">
                  {event.PhasePrintDescription || event.PhaseDescription || event.PhaseCode || ''}
                </div>
              </div>
              <div className="event-details">
                <div className="detail">
                  <span className="label">Time:</span>
                  <span className="value">{formatEventTimeRange(event)}</span>
                </div>
                {actualTimingNote && (
                  <div className="detail detail--subtle">
                    <span className="label">Update:</span>
                    <span className="value">{actualTimingNote}</span>
                  </div>
                )}
                {event.UnitDescription && (
                  <div className="detail">
                    <span className="label">Session:</span>
                    <span className="value">{event.UnitDescription}</span>
                  </div>
                )}
                {showLocation && (event.LocationDescription || event.VenueDescription) && (
                  <div className="detail">
                    <span className="label">Location:</span>
                    <span className="value">{event.LocationDescription || event.VenueDescription}</span>
                  </div>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>

      {
        <div className={`schedule-scroll-bar schedule-scroll-bar--controls-only ${canScrollDown ? 'schedule-scroll-bar--has-below' : ''}`.trim()}>
          {hasScrollableEvents && (
            <div className="schedule-scroll-bar__controls">
              <button
                type="button"
                className="schedule-scroll-bar__btn"
                onClick={() => nudgeEventsList(-1)}
                disabled={!canScrollUp}
                aria-label="Scroll event list up"
              >
                <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M8 3L14 11H2Z" />
                </svg>
              </button>
              <button
                type="button"
                className="schedule-scroll-bar__btn"
                onClick={() => nudgeEventsList(1)}
                disabled={!canScrollDown}
                aria-label="Scroll event list down"
              >
                <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M8 13L2 5h12Z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      }
    </div>
  );
};
